import { BasePublicApi } from '#packages/apilib';
import { DESKTOP_MIN_RANGE, MOBILE_MIN_RANGE } from './consts';
import { getBreakpoint } from './breakpointsUtils';

import type { Scope } from './breakpointsEntryPoint';
import type { BreakpointsDsData } from '@wix/document-services-types';
import type { BreakpointData } from './types';
import experiment from 'experiment';
import { EditorParamsApiKey } from '#packages/apis';

const getFocusedPageDesktopBreakpoint = ({
  editorAPI,
}: Scope): BreakpointData => {
  const focusedPage = editorAPI.pages.getFocusedPage();
  return getBreakpoint(editorAPI, focusedPage, DESKTOP_MIN_RANGE);
};

const getFocusedPageMobileBreakpoint = ({
  editorAPI,
}: Scope): BreakpointData => {
  const focusedPage = editorAPI.pages.getFocusedPage();
  return getBreakpoint(editorAPI, focusedPage, MOBILE_MIN_RANGE);
};

const getFocusedPageBreakpoints = ({ editorAPI }: Scope): BreakpointsDsData => {
  const focusedPage = editorAPI.pages.getFocusedPage();
  return editorAPI.breakpoints.get(focusedPage);
};

export const isFluidEditor = (scope: Scope): boolean => {
  const { isInsideEditorX } = scope.editorAPI.host.getAPI(EditorParamsApiKey);
  // TODO: hack for .spec files which use old ds testkit, need to fix it
  if (
    isInsideEditorX ||
    !experiment.isOpen('se_mobileOverUnifiedStructure') ||
    !scope.editorAPI.breakpoints
  ) {
    return false;
  }

  // TODO: Use isFluidEditor with documentType - https://jira.wixpress.com/browse/DM-10738
  return getFocusedPageBreakpoints(scope)?.values?.length > 0;
};

const isDesktopBreakpointActive = ({ store }: Scope): boolean => {
  return store.getIsDesktopBreakpointActive();
};

const isMobileBreakpointActive = ({ store }: Scope): boolean => {
  return store.getIsMobileBreakpointActive();
};

const getFocusedPageActiveBreakpoint = (
  scope: Scope,
): BreakpointData | null => {
  if (!isFluidEditor(scope)) {
    return null;
  }
  if (scope.store.getIsDesktopBreakpointActive()) {
    return getFocusedPageDesktopBreakpoint(scope);
  } else if (scope.store.getIsMobileBreakpointActive()) {
    return getFocusedPageMobileBreakpoint(scope);
  }
  return null;
};

export const setMobileBreakpoint = (scope: Scope) => {
  const mobileBreakpoint = getFocusedPageMobileBreakpoint(scope);
  return scope.store.setActiveBreakpoint(mobileBreakpoint.value);
};

export const setDesktopBreakpoint = (scope: Scope) => {
  const desktopBreakpoint = getFocusedPageDesktopBreakpoint(scope);
  return scope.store.setActiveBreakpoint(desktopBreakpoint.value);
};

export class BreakpointsApi extends BasePublicApi<Scope> {
  getFocusedPageBreakpoints = this.bindScope(getFocusedPageBreakpoints);
  getFocusedPageMobileBreakpoint = this.bindScope(
    getFocusedPageMobileBreakpoint,
  );
  getFocusedPageDesktopBreakpoint = this.bindScope(
    getFocusedPageDesktopBreakpoint,
  );
  isDesktopBreakpointActive = this.bindScope(isDesktopBreakpointActive);
  isMobileBreakpointActive = this.bindScope(isMobileBreakpointActive);
  getFocusedPageActiveBreakpoint = this.bindScope(
    getFocusedPageActiveBreakpoint,
  );
  setMobileBreakpoint = this.bindScope(setMobileBreakpoint);
  setDesktopBreakpoint = this.bindScope(setDesktopBreakpoint);
}
